import 'ol/ol.css'
import './style.css'

import Map from 'ol/Map'
import View from 'ol/View'
import Attribution from 'ol/control/Attribution'
import ScaleLine from 'ol/control/ScaleLine'
import MousePosition from 'ol/control/MousePosition'
import Zoom from 'ol/control/Zoom'
import { createStringXY } from 'ol/coordinate'
import { defaults as defaultInteractions } from 'ol/interaction'
import DragPan from 'ol/interaction/DragPan'
import Kinetic from 'ol/Kinetic'
import { noModifierKeys, primaryAction } from 'ol/events/condition'
import type MapBrowserEvent from 'ol/MapBrowserEvent'

import { api, type Project, type LayerInfo, type Legend, type LegendItem,
         type FeatureDetail } from './api'
import { OpsLayer, type PickHit } from './opsLayer'
import { projectionFor } from './proj'
import { setBasemap, PODKLADY, type Podklad } from './basemap'
import { AttrTable } from './table'
import { themeToggleHTML, wireThemeToggle, watchSystemTheme } from './theme'
import { esc, nf, plural, OBIEKT, store, restore,
         storeSession, restoreSession } from './util'
import { pytanie } from './dialog'

const app = document.getElementById('app')!
const RES_TO_DENOM = 96 / 0.0254          // mianownik skali z rozdzielczosci OL

/** Najblizsze zblizenie przy przyblizaniu do obiektu z tabeli. */
const MIN_FIT_DENOM = 250

/** Logotyp GIS Mates - ten sam plik sluzy za favikone (public/gis-mates.png). */
const LOGO = '/gis-mates.png'

/** Ikony interfejsu; rysowane obrysem, barwa idzie z currentColor. */
const ICON = {
  back: `<svg viewBox="0 0 16 16" aria-hidden="true">
    <path d="M13 8H3.6M7.6 3.8 3.4 8l4.2 4.2"/></svg>`,
  pan: `<svg viewBox="0 0 16 16" aria-hidden="true">
    <path d="M8 2.4v11.2M2.4 8h11.2M6.3 4.1 8 2.4l1.7 1.7M6.3 11.9 8 13.6l1.7-1.7
             M4.1 6.3 2.4 8l1.7 1.7M11.9 6.3 13.6 8l-1.7 1.7"/></svg>`,
  identify: `<svg viewBox="0 0 16 16" aria-hidden="true">
    <path d="M3.5 2.5 11.5 9.2l-3.9.4 2 3.8-1.7.7-1.9-3.7-2.5 2.4z"/></svg>`,
  fit: `<svg viewBox="0 0 16 16" aria-hidden="true">
    <path d="M2 5.6V3a1 1 0 0 1 1-1h2.6M10.4 2H13a1 1 0 0 1 1 1v2.6
             M14 10.4V13a1 1 0 0 1-1 1h-2.6M5.6 14H3a1 1 0 0 1-1-1v-2.6"/>
    <rect x="5.5" y="5.5" width="5" height="5" rx=".7"/></svg>`,
  // Kartka z liniami tekstu - zestawienie, a nie tabela: bez pionowej kreski,
  // ktora w ikonie tabeli oznacza podzial na kolumny.
  report: `<svg viewBox="0 0 16 16" aria-hidden="true">
    <path d="M3.4 2.4h6.4l2.8 2.8v8.4H3.4z"/><path d="M9.6 2.4v3h3"/>
    <path d="M5.6 8h5M5.6 10.6h5"/></svg>`,
  download: `<svg viewBox="0 0 16 16" aria-hidden="true">
    <path d="M8 2.4v8.2m0 0L4.8 7.4M8 10.6l3.2-3.2"/>
    <path d="M2.6 12.2v.9a1.4 1.4 0 0 0 1.4 1.4h8a1.4 1.4 0 0 0 1.4-1.4v-.9"/></svg>`,
  // Siatka z wyrozniona pierwsza kolumna i wierszem naglowka - tabela, a nie
  // zwykly prostokat podzielony na kratki.
  table: `<svg viewBox="0 0 16 16" aria-hidden="true">
    <rect x="2.2" y="3.2" width="11.6" height="9.6" rx="1"/>
    <path d="M2.2 6.4h11.6M6.4 6.4v6.4M10 6.4v6.4"/></svg>`,
  eye: `<svg viewBox="0 0 16 16" aria-hidden="true">
    <path d="M1.5 8s2.4-4.3 6.5-4.3S14.5 8 14.5 8s-2.4 4.3-6.5 4.3S1.5 8 1.5 8z"/>
    <circle cx="8" cy="8" r="1.9"/></svg>`,
  eyeOff: `<svg viewBox="0 0 16 16" aria-hidden="true">
    <path d="M1.5 8s2.4-4.3 6.5-4.3S14.5 8 14.5 8s-2.4 4.3-6.5 4.3S1.5 8 1.5 8z"/>
    <circle cx="8" cy="8" r="1.9"/><path d="M2.6 2.6 13.4 13.4"/></svg>`,
  // Sama para chevronow czyta sie w tym rozmiarze jako krzyzyk albo romb -
  // dopiero kreska w srodku mowi, do czego (albo od czego) one zmierzaja.
  collapse: `<svg viewBox="0 0 16 16" aria-hidden="true">
    <path d="M3.6 2.6 8 5.5l4.4-2.9M3.2 8h9.6M3.6 13.4 8 10.5l4.4 2.9"/></svg>`,
  expand: `<svg viewBox="0 0 16 16" aria-hidden="true">
    <path d="M3.6 5.5 8 2.6l4.4 2.9M3.2 8h9.6M3.6 10.5 8 13.4l4.4-2.9"/></svg>`,
  chevron: `<svg class="chev" viewBox="0 0 12 12" aria-hidden="true"><path d="M3 4.5 6 8l3-3.5"/></svg>`,
}

// -------------------------------------------------- szerokosci paneli

interface Pane {
  css: string; key: string; def: number; min: number; max: number
  /** dir: w ktora strone rosnie panel, gdy uchwyt jedzie w prawo (albo w dol). */
  dir: number
  /** Os, po ktorej jedzie uchwyt. */
  axis: 'x' | 'y'
}

const PANE: Record<'rail' | 'panel' | 'table', Pane> = {
  rail:  { css: '--rail-w',  key: 'gmlweb.railW',  def: 258, min: 180, max: 620, dir: 1,  axis: 'x' },
  panel: { css: '--panel-w', key: 'gmlweb.panelW', def: 308, min: 200, max: 720, dir: -1, axis: 'x' },
  table: { css: '--table-h', key: 'gmlweb.tableH', def: 260, min: 120, max: 800, dir: -1, axis: 'y' },
}

function applyStoredWidths() {
  for (const p of Object.values(PANE)) {
    try {
      const v = Number(localStorage.getItem(p.key))
      if (v >= p.min && v <= p.max) document.documentElement.style.setProperty(p.css, `${v}px`)
    } catch { /* prywatny tryb przegladarki */ }
  }
}

/**
 * Uchwyty miedzy panelami a mapa.
 *
 * onResize wola updateSize i doladowanie: zmiana szerokosci nie rusza widoku,
 * wiec OpenLayers nie wyemituje moveend i bez tego mapa zostalaby z danymi dla
 * poprzedniego kadru.
 */
function setupGrips(root: ParentNode, onResize: () => void) {
  const wire = (sel: string, p: Pane) => {
    const grip = root.querySelector<HTMLElement>(sel)
    if (!grip) return
    const width = () =>
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue(p.css)) || p.def
    const setW = (w: number) => {
      const v = Math.round(Math.min(p.max, Math.max(p.min, w)))
      document.documentElement.style.setProperty(p.css, `${v}px`)
      try { localStorage.setItem(p.key, String(v)) } catch { /* j.w. */ }
    }

    const at = (e: PointerEvent) => p.axis === 'x' ? e.clientX : e.clientY

    grip.addEventListener('pointerdown', e => {
      e.preventDefault()
      grip.setPointerCapture(e.pointerId)
      grip.classList.add('dragging')
      const x0 = at(e)
      const w0 = width()
      const move = (ev: PointerEvent) => { setW(w0 + p.dir * (at(ev) - x0)); onResize() }
      const up = () => {
        grip.classList.remove('dragging')
        grip.removeEventListener('pointermove', move)
        grip.removeEventListener('pointerup', up)
        onResize()
      }
      grip.addEventListener('pointermove', move)
      grip.addEventListener('pointerup', up)
    })

    grip.ondblclick = () => { setW(p.def); onResize() }
    grip.onkeydown = e => {
      const [less, more] = p.axis === 'x' ? ['ArrowLeft', 'ArrowRight'] : ['ArrowUp', 'ArrowDown']
      const step = e.key === less ? -16 : e.key === more ? 16 : 0
      if (!step) return
      e.preventDefault()
      setW(width() + p.dir * step)
      onResize()
    }
  }
  wire('.grip.left', PANE.rail)
  wire('.grip.right', PANE.panel)
  wire('.grip.row', PANE.table)
}

// ------------------------------------------------------------- narzedzie

type Tool = 'pan' | 'identify'
const TOOL_KEY = 'gmlweb.tool'

function storedTool(): Tool {
  try {
    const v = localStorage.getItem(TOOL_KEY)
    if (v === 'pan' || v === 'identify') return v
  } catch { /* prywatny tryb przegladarki */ }
  return 'identify'
}

const TOOL_LABEL: Record<Tool, string> = {
  pan: 'Przesuwanie mapy',
  identify: 'Identyfikacja obiektów',
}

const toolsHTML = (cur: Tool) => `
  <div class="seg tools" role="group" aria-label="Narzędzie mapy">${
    (['pan', 'identify'] as Tool[]).map(t =>
      `<button type="button" data-tool="${t}" title="${TOOL_LABEL[t]}"
               aria-label="${TOOL_LABEL[t]}" aria-pressed="${t === cur}">${ICON[t]}</button>`).join('')
  }</div>`

const lockup = (small = false) => `
  <a class="lockup" href="?" title="gmlweb — projekty">
    <img class="mark${small ? ' sm' : ''}" src="${LOGO}" alt="" width="30" height="30">
    ${small ? '' : '<span class="wordmark"><b>gmlweb</b><em>GIS Mates</em></span>'}
  </a>`

// ------------------------------------------------------- stan widoku

/**
 * Co projekt pamieta miedzy wejsciami: wycinek mapy, zapalone warstwy,
 * etykiety i otwarty dok tabeli.
 *
 * Powrot z listy do tego samego projektu ma zastac widok tam, gdzie zostal -
 * przy mapie zasadniczej dojscie do wlasciwego kwartalu i zapalenie kilkunastu
 * warstw to praca, ktorej nie ma po co powtarzac. Stan siedzi w sessionStorage,
 * wiec zyje tyle co karta przegladarki i nie przenosi sie miedzy projektami.
 */
interface WidokProjektu {
  c: number[]          // srodek mapy w ukladzie projektu
  r: number            // rozdzielczosc (metry na piksel)
  v: string[]          // zapalone warstwy
  lbl: boolean         // etykiety
  tbl: boolean         // otwarty dok tabeli atrybutow
}

const widokKey = (id: string) => `gmlweb.widok.${id}`

function wczytajWidok(id: string): WidokProjektu | null {
  const raw = restoreSession(widokKey(id))
  if (!raw) return null
  try {
    const w = JSON.parse(raw) as WidokProjektu
    return Array.isArray(w.c) && w.c.length === 2 && typeof w.r === 'number' ? w : null
  } catch {
    return null
  }
}

// ---------------------------------------------------------------- routing

function projectIdFromUrl(): string | null {
  return new URLSearchParams(location.search).get('p')
}

async function boot() {
  watchSystemTheme()
  applyStoredWidths()
  const id = projectIdFromUrl()
  if (id) await showMap(id)
  else await showHome()
}

// ------------------------------------------------------------------ home

async function showHome() {
  app.innerHTML = `
    <div class="home">
      <header class="home-top">
        ${lockup()}
        <span class="spacer"></span>
        ${themeToggleHTML()}
      </header>

      <section class="hero">
        <h1><span>Mapa zasadnicza prosto z pliku GML</span></h1>
        <p>Wczytaj plik GML (EGiB / GESUT / BDOT500) i obejrzyj go na mapie
           w oryginalnym układzie, z symboliką liczoną przez QGIS.</p>
      </section>

      <label class="drop" id="drop">
        <input type="file" id="file" accept=".gml,.xml,.zip" hidden>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 15.5V4m0 0L8 8m4-4 4 4"/>
          <path d="M3.5 15v3.2A1.8 1.8 0 0 0 5.3 20h13.4a1.8 1.8 0 0 0 1.8-1.8V15"/>
        </svg>
        <strong>Wybierz plik GML</strong>
        <span>albo upuść go tutaj</span>
        <em>.gml · .xml · .zip</em>
      </label>

      <div class="opts">
        <label class="switch" title="Obiekty z datą końca (koniecObiektu) trafiają do warstw _ARCH">
          <input type="checkbox" id="arch" checked>
          <span class="track" aria-hidden="true"></span>
          <span>wczytaj warstwy archiwalne</span>
        </label>
        <span class="hint">Pominięte warstwy można odzyskać wyłącznie przez ponowne
          wczytanie pliku. Spakowany GML przesyła się kilkanaście razy szybciej;
          archiwum powinno zawierać jeden plik.</span>
      </div>

      <div id="msg" class="msg"></div>

      <h2 class="sec">Projekty</h2>
      <p class="scope">Lista projektów jest powiązana z tą przeglądarką — serwis nie
         zakłada kont i nie prowadzi archiwum. Plik źródłowy GML służy wyłącznie do
         wczytania danych i zostaje usunięty zaraz po ich przetworzeniu; nie jest
         nigdzie przechowywany. Projekt nieotwierany przez 7 dni zostaje usunięty
         wraz z danymi; wyczyszczenie ciasteczek kasuje listę natychmiast.</p>
      <div id="list" class="list"></div>
    </div>`

  wireThemeToggle(app)

  const input = app.querySelector<HTMLInputElement>('#file')!
  const drop = app.querySelector<HTMLLabelElement>('#drop')!
  const msg = app.querySelector<HTMLDivElement>('#msg')!

  // iOS tlumaczy accept na systemowe typy plikow. .gml takiego typu nie ma,
  // wiec picker wyszarza pliki GML i daje wybrac tylko .zip/.xml. Na iOS filtr
  // znika, a rozszerzenie sprawdza send(). iPadOS przedstawia sie jako Mac -
  // zdradza go dopiero ekran dotykowy.
  const iOS = /iPhone|iPad|iPod/.test(navigator.userAgent)
    || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
  if (iOS) input.removeAttribute('accept')

  // Po wgraniu ZOSTAJEMY na liscie. Import trwa i tak, a karta projektu pokazuje
  // postep i sama sie odswieza - przeskok na ekran mapy odbieral mozliwosc
  // wczytania kolejnego pliku i zmuszal do powrotu przyciskiem wstecz.
  const send = async (f: File) => {
    // Serwer i tak odrzuci inne rozszerzenie, ale dopiero po wyslaniu calego
    // pliku. Bez filtru w pickerze (iOS) i przy upuszczaniu to realne ryzyko.
    if (!/\.(gml|xml|zip)$/i.test(f.name)) {
      msg.className = 'msg err'
      msg.textContent = `Nieobsługiwany plik ${f.name} — oczekiwano .gml, .xml albo .zip.`
      input.value = ''
      return
    }
    msg.className = 'msg'
    msg.innerHTML = `<div class="up" role="status">
        <span class="up-t">Wysyłanie <b>${esc(f.name)}</b> · ${mb(f.size)}</span>
        <span class="up-p">0%</span>
        <span class="bar"><i style="width:0"></i></span>
      </div>`
    const up = msg.querySelector<HTMLElement>('.up')!
    const tytul = up.querySelector<HTMLElement>('.up-t')!
    const proc = up.querySelector<HTMLElement>('.up-p')!
    const pasek = up.querySelector<HTMLElement>('.bar i')!

    // Drugi plik w trakcie wysylania pierwszego to dwa strumienie walczace
    // o to samo lacze i dwa paski postepu w jednym miejscu.
    input.disabled = true
    drop.classList.add('busy')
    try {
      const arch = app.querySelector<HTMLInputElement>('#arch')!.checked
      await api.upload(f, arch, (wyslane, calosc) => {
        const p = Math.round(wyslane / calosc * 100)
        proc.textContent = `${p}%`
        pasek.style.width = `${p}%`
        // Ostatni bajt na laczu to nie koniec czekania: serwer zapisuje plik
        // na dysk i zaklada projekt, a przy pol gigabajta to jeszcze chwila.
        // Pasek przechodzi wtedy w ruch nieokreslony.
        if (p >= 100) {
          up.classList.add('idet')
          tytul.textContent = `${f.name} — zakładanie projektu…`
          proc.textContent = ''
        }
      })
      msg.className = 'msg'
      msg.textContent = `Wczytywanie ${f.name} — postęp na liście poniżej.`
      input.value = ''            // ten sam plik da sie wybrac drugi raz
      await refreshList()
    } catch (e) {
      msg.className = 'msg err'
      msg.textContent = `Błąd: ${czytelnyBlad(e)}`
    } finally {
      input.disabled = false
      drop.classList.remove('busy')
    }
  }

  input.onchange = () => input.files?.[0] && send(input.files[0])
  drop.ondragover = e => { e.preventDefault(); drop.classList.add('over') }
  drop.ondragleave = () => drop.classList.remove('over')
  drop.ondrop = e => {
    e.preventDefault()
    drop.classList.remove('over')
    const f = e.dataTransfer?.files?.[0]
    if (f) send(f)
  }

  await refreshList()
}

async function refreshList() {
  const box = app.querySelector<HTMLDivElement>('#list')
  if (!box) return
  const projects = await api.list()
  if (!projects.length) {
    box.innerHTML = '<p class="empty">Brak projektów — wczytaj pierwszy plik GML.</p>'
    return
  }

  box.innerHTML = projects.map(p => {
    const busy = p.status !== 'ready' && p.status !== 'error'
    const pct = Math.round((p.progress ?? 0) * 100)
    return `
    <article class="card ${busy ? 'busy' : p.status}">
      <span class="dot"></span>
      <a class="nm" href="?p=${p.id}">${esc(p.name)}</a>
      <span class="pill">${esc(statusPill(p))}</span>
      ${p.stats?.qgis_export ? `
      <a class="dl" href="${api.exportUrl(p.id)}" download
         title="Pobierz projekt QGIS (${mb(p.stats.qgis_export.bytes)})"
         aria-label="Pobierz projekt QGIS dla ${esc(p.name)}">${ICON.download}</a>`
      : '<span class="dl-none"></span>'}
      <button class="del" data-del="${p.id}" title="Usuń projekt"
              aria-label="Usuń ${esc(p.name)}">×</button>
      <span class="meta">${statusMeta(p)}</span>
      ${busy ? `<span class="prog"><i style="width:${pct}%"></i></span>` : ''}
    </article>`
  }).join('')

  box.querySelectorAll<HTMLButtonElement>('[data-del]').forEach(b => {
    b.onclick = async () => {
      const card = b.closest('.card') as HTMLElement
      const name = card.querySelector('.nm')!.textContent
      const zgoda = await pytanie({
        tytul: 'Usunąć projekt?',
        tresc: `„${name}” zostanie usunięty razem z wczytanymi danymi. `
             + 'Przywrócenie go wymaga ponownego wczytania pliku.',
        potwierdz: 'Usuń projekt',
        grozne: true,
      })
      if (!zgoda) return

      // Skasowanie projektu to setki tysiecy wierszy i przy pliku powiatowym
      // trwa - bez sladu na karcie panel wyglada przez ten czas na zawieszony.
      usuwane++
      card.classList.add('removing')
      card.querySelector('.pill')!.textContent = 'usuwanie…'
      try {
        await api.remove(b.dataset.del!)
        card.classList.add('gone')
        await new Promise(r => setTimeout(r, 200))    // czas na zniknięcie karty
      } catch (e) {
        card.classList.remove('removing')
        const msg = app.querySelector<HTMLDivElement>('#msg')
        if (msg) {
          msg.className = 'msg err'
          msg.textContent = `Nie udało się usunąć projektu: ${czytelnyBlad(e)}`
        }
      } finally {
        usuwane--
      }
      refreshList()
    }
  })

  // Odswiezanie listy w trakcie kasowania przerysowaloby karte razem z jej
  // stanem "usuwanie" - czekamy, az skonczy sie ostatnie.
  if (!usuwane && projects.some(p => p.status !== 'ready' && p.status !== 'error')) {
    setTimeout(refreshList, 1500)
  }
}

/** Ile kart czeka wlasnie na skasowanie - patrz refreshList(). */
let usuwane = 0

function statusPill(p: Project): string {
  if (p.status === 'ready') return 'gotowy'
  if (p.status === 'error') return 'błąd'
  return p.stage ?? p.status
}

function statusMeta(p: Project): string {
  const size = mb(p.size_bytes ?? 0)
  if (p.status === 'error') return esc(p.error ?? 'nieznany błąd')
  if (p.status === 'ready') {
    const s = p.stats as {
      features?: number; labels?: number; skipped_arch?: number
      import_s?: number; bake_s?: number; total_s?: number
    } | null
    // Pominiete warstwy archiwalne trzeba pokazac, inaczej nie wiadomo, czemu
    // projekt ich nie ma - a odzyskac je mozna tylko wczytujac plik od nowa.
    const arch = s?.skipped_arch
      ? ` · <b>bez ${nf.format(s.skipped_arch)}</b> warstw archiwalnych` : ''
    return `<b>${nf.format(s?.features ?? 0)}</b> obiektów ·
            <b>${nf.format(s?.labels ?? 0)}</b> etykiet · ${size}${arch}${czasWczytania(s)}${wygasa(p)}`
  }
  return `${Math.round((p.progress ?? 0) * 100)}% · ${size}`
}

/**
 * Ile trwalo wczytanie projektu.
 *
 * Podpowiedz rozbija to na dwie czesci, bo wypiekanie symboliki bywa kilka razy
 * dluzsze od samego wczytania GML-a - bez tego liczba nic nie tlumaczy.
 * Projekty wczytane przed wprowadzeniem pomiaru nie maja tych pol i nie
 * pokazujemy dla nich niczego.
 */
function czasWczytania(s: { import_s?: number; bake_s?: number; total_s?: number } | null): string {
  if (typeof s?.total_s !== 'number') return ''
  const podpowiedz = typeof s.import_s === 'number' && typeof s.bake_s === 'number'
    ? ` title="wczytanie GML do bazy: ${trwanie(s.import_s)} · symbolika: ${trwanie(s.bake_s)}"`
    : ''
  return ` · <span class="dur"${podpowiedz}>wczytany w <b>${trwanie(s.total_s)}</b></span>`
}

/** Czas trwania po polsku: 42 s, 5 min 12 s, 1 h 6 min. */
function trwanie(sekundy: number): string {
  const s = Math.max(0, Math.round(sekundy))
  if (s < 60) return `${s} s`
  const m = Math.floor(s / 60)
  if (m < 60) return s % 60 ? `${m} min ${s % 60} s` : `${m} min`
  const h = Math.floor(m / 60)
  return m % 60 ? `${h} h ${m % 60} min` : `${h} h`
}

/**
 * Ile zostalo projektowi zycia.
 *
 * Ostatni dzien wyrozniony, bo to jedyny moment, w ktorym uzytkownik moze
 * jeszcze zareagowac - otwarcie projektu odsuwa termin o kolejny tydzien.
 */
function wygasa(p: Project): string {
  if (!p.expires_at) return ''
  const dni = Math.ceil((Date.parse(p.expires_at) - Date.now()) / 86400000)
  if (!Number.isFinite(dni)) return ''
  if (dni <= 0) return ' · <b class="warn">wygasa dziś</b>'
  if (dni === 1) return ' · <b class="warn">wygasa jutro</b>'
  return ` · wygasa za ${plural(dni, DNI)}`
}

const DNI = { one: 'dzień', few: 'dni', many: 'dni', other: 'dnia' }

const mb = (b: number) => `${(b / 1048576).toFixed(1)} MB`

/**
 * Komunikat serwera zamiast surowej odpowiedzi.
 *
 * Odrzucone wgranie niesie konkretny powod ("archiwum zawiera 5 plikow GML"),
 * ale opakowany w `Error: 400 {"detail":...}` - a to uzytkownikowi nie mowi
 * nic poza tym, ze cos poszlo nie tak.
 */
function czytelnyBlad(e: unknown): string {
  const tekst = String(e)
  const nawias = tekst.indexOf('{')
  if (nawias < 0) return tekst
  try {
    const detail = JSON.parse(tekst.slice(nawias)).detail
    return typeof detail === 'string' ? detail : tekst
  } catch {
    return tekst
  }
}

// ------------------------------------------------------------------- mapa

async function showMap(id: string) {
  // Skasowany projekt zostawiony w zakladce albo w historii dawal pusty ekran:
  // wyjatek leciał z boot() i nikt go nie lapal.
  let project: Project
  try {
    project = await api.get(id)
  } catch (e) {
    app.innerHTML = `<div class="wait">
        <img class="mark" src="${LOGO}" alt="" width="44" height="44">
        <h2>Nie ma takiego projektu</h2>
        <p>Mógł zostać usunięty.</p>
        <pre>${esc(String(e))}</pre>
        <a href="?">← projekty</a>
      </div>`
    return
  }

  while (project.status !== 'ready' && project.status !== 'error') {
    app.innerHTML = `<div class="wait">
        <img class="mark" src="${LOGO}" alt="" width="44" height="44">
        <h2>${esc(project.name)}</h2>
        <p>${esc(project.stage ?? project.status)}</p>
        <div class="bar"><i style="width:${Math.round((project.progress ?? 0) * 100)}%"></i></div>
        <a href="?">← projekty</a>
      </div>`
    await new Promise(r => setTimeout(r, 1200))
    project = await api.get(id)
  }

  if (project.status === 'error') {
    app.innerHTML = `<div class="wait">
        <img class="mark" src="${LOGO}" alt="" width="44" height="44">
        <h2>Błąd importu</h2>
        <pre>${esc(project.error ?? '')}</pre>
        <a href="?">← projekty</a>
      </div>`
    return
  }

  const srid = project.srid!
  const extent = project.extent!
  const layers = (project.layers ?? []).filter(l => l.geom_type !== 'None')
  const archLayers = layers.filter(l => isArch(l.name))
  let tool = storedTool()
  const zapamietanyWidok = wczytajWidok(id)

  app.innerHTML = `
    <div class="ui" data-tool="${tool}">
      <header class="top">
        <a class="back" href="?" title="Projekty"
           aria-label="Wróć do projektów">${ICON.back}</a>
        ${lockup(true)}
        <h1>${esc(project.name)}</h1>
        <span class="chip mono">EPSG:${srid}</span>
        <div class="maptools">
          ${toolsHTML(tool)}
          <button class="icon-btn" id="fit" type="button"
                  title="Dopasuj do zasięgu danych" aria-label="Dopasuj do zasięgu danych"
                  >${ICON.fit}</button>
        </div>
        <span class="spacer"></span>
        <div class="datatools" role="group" aria-label="Dane projektu">
          <button class="icon-btn" id="tbl-btn" type="button" aria-pressed="false"
                  title="Tabela atrybutów" aria-label="Pokaż tabelę atrybutów"
                  >${ICON.table}</button>
          <button class="icon-btn" id="rep-btn" type="button" hidden
                  title="Zestawienia EGiB" aria-label="Pokaż zestawienia EGiB"
                  >${ICON.report}</button>
        </div>
        ${project.stats?.qgis_export ? `
        <a class="dl-btn" href="${api.exportUrl(id)}" download
           title="Projekt QGIS z danymi, symboliką i symbolami (${mb(project.stats.qgis_export.bytes)})"
           aria-label="Pobierz projekt QGIS"
           >${ICON.download}<span>Projekt QGIS</span></a>` : ''}
        ${themeToggleHTML()}
      </header>
      <aside class="rail">
        <div class="rail-top">
        <div class="rail-h">Warstwy <span class="spacer"></span>
          <button class="icon-btn sm" id="all" type="button"
                  title="Ukryj wszystkie warstwy" aria-label="Ukryj wszystkie warstwy"
                  >${ICON.eye}</button>
          <button class="icon-btn sm" id="collapse" type="button"
                  title="Zwiń wszystko" aria-label="Zwiń wszystkie grupy i klasy"
                  >${ICON.collapse}</button>
          <button class="icon-btn sm" id="expand" type="button"
                  title="Rozwiń wszystko" aria-label="Rozwiń wszystkie grupy i klasy"
                  >${ICON.expand}</button>
        </div>
        <div class="rail-opts">
          <label class="pick" title="Obraz pod mapą zasadniczą. Domyślnie OpenStreetMap,
pobierany z serwera zewnętrznego — dane projektu nigdzie przy tym nie wychodzą.
Wybór „brak” wyłącza wszelkie odpytywanie obcych serwerów.">
            <span>podkład</span>
            <select id="basemap">${PODKLADY.map(p =>
              `<option value="${p.id}">${esc(p.nazwa)}</option>`).join('')}</select>
          </label>
          <label class="switch" title="Pokaż etykiety obiektów">
            <input type="checkbox" id="lbl" ${zapamietanyWidok?.lbl === false ? '' : 'checked'}>
            <span class="track" aria-hidden="true"></span>
            <span>etykiety</span>
          </label>
          ${archLayers.length ? `
          <label class="switch" title="Pokaż ${archLayers.length} warstw archiwalnych (_ARCH)">
            <input type="checkbox" id="arch-vis">
            <span class="track" aria-hidden="true"></span>
            <span>archiwalne</span>
          </label>` : ''}
        </div>
        </div>
        <div id="lyrs"></div>
      </aside>
      <main id="map"></main>
      <section class="panel" id="panel">
        <div class="ph">Kliknij obiekt na mapie, aby zobaczyć jego atrybuty.</div>
      </section>
      <section class="tbl" id="tbl" hidden aria-label="Tabela atrybutów"></section>
      <div class="grip left" role="separator" aria-orientation="vertical" tabindex="0"
           title="Przeciągnij, aby zmienić szerokość (dwuklik przywraca domyślną)"
           aria-label="Szerokość panelu warstw"></div>
      <div class="grip right" role="separator" aria-orientation="vertical" tabindex="0"
           title="Przeciągnij, aby zmienić szerokość (dwuklik przywraca domyślną)"
           aria-label="Szerokość panelu atrybutów"></div>
    </div>`

  wireThemeToggle(app)

  const projection = projectionFor(srid)
  const opsLayer = new OpsLayer()

  const map = new Map({
    target: 'map',
    layers: [opsLayer],
    view: new View({ projection, extent, constrainOnlyCenter: true, showFullExtent: true }),
    controls: [
      new Zoom({ zoomInTipLabel: 'Przybliż', zoomOutTipLabel: 'Oddal' }),
      new ScaleLine({ units: 'metric', bar: true, text: true, minWidth: 90 }),
      // Zrodla podkladu. Lista kontrolek jest u nas podana wprost, wiec
      // domyslnej kontrolki atrybucji tu nie ma - a bez niej podklad OSM
      // lamalby warunki licencji, a ortofotomapa wymog podania GUGiK-u.
      // Przy podkladzie 'brak' nie ma czego pokazac i kontrolka jest pusta.
      new Attribution({ collapsible: false }),
      // placeholder '' zamiast domyslnej spacji - inaczej pusta ramka wisi
      // w rogu mapy, dopoki kursor na nia nie wjedzie (.mouse-pos:empty)
      new MousePosition({ coordinateFormat: createStringXY(1), projection,
                          className: 'mouse-pos', placeholder: '' }),
    ],
    // Wlasny DragPan zamiast domyslnego, bo warunek zalezy od narzedzia.
    interactions: defaultInteractions({ altShiftDragRotate: false, pinchRotate: false,
                                        dragPan: false }),
  })

  // Stan doku tabeli trzymamy obok, zeby zapis widoku nie musial siegac do
  // AttrTable - ta powstaje duzo pozniej niz pierwszy zapis.
  let tblOtwarta = zapamietanyWidok?.tbl ?? false

  /** Migawka widoku dla powrotu z listy projektow - patrz WidokProjektu. */
  function zapiszWidok() {
    const view = map.getView()
    const c = view.getCenter()
    const r = view.getResolution()
    if (!c || !r) return
    storeSession(widokKey(id), JSON.stringify({
      c: [c[0], c[1]], r, v: [...visible], lbl: opsLayer.showLabels, tbl: tblOtwarta,
    } satisfies WidokProjektu))
  }

  /**
   * Kolko myszy przesuwa mape zawsze, niezaleznie od wybranego narzedzia -
   * tak samo jak w QGIS-ie. Lewy przycisk przesuwa tylko w trybie
   * przesuwania; w trybie identyfikacji nalezy do wskazywania obiektow.
   *
   * W trakcie ciagniecia PointerEvent.button ma -1, wiec srodkowy przycisk
   * poznajemy po masce buttons (bit 4), a nie po samym button.
   */
  const panCondition = (evt: MapBrowserEvent<PointerEvent | KeyboardEvent | WheelEvent>) => {
    const e = evt.originalEvent as PointerEvent
    if (e.button === 1 || (e.buttons & 4) !== 0) return true
    return tool === 'pan' && noModifierKeys(evt) && primaryAction(evt)
  }
  map.addInteraction(new DragPan({ condition: panCondition,
                                   kinetic: new Kinetic(-0.005, 0.05, 100) }))

  // Bez tego Windows odpala na srodkowy przycisk wlasne przewijanie strony.
  const viewport = map.getViewport()
  viewport.addEventListener('mousedown', e => { if (e.button === 1) e.preventDefault() })
  viewport.addEventListener('auxclick', e => e.preventDefault())
  const fit = () => map.getView().fit(extent, { padding: [24, 24, 24, 24], duration: 260 })
  if (zapamietanyWidok) {
    map.getView().setCenter(zapamietanyWidok.c)
    map.getView().setResolution(zapamietanyWidok.r)
  } else {
    map.getView().fit(extent, { padding: [24, 24, 24, 24] })
  }
  document.getElementById('fit')!.onclick = fit

  // ---- wybor narzedzia
  const toolBtns = app.querySelectorAll<HTMLButtonElement>('.tools button[data-tool]')
  toolBtns.forEach(b => {
    b.onclick = () => {
      tool = b.dataset.tool as Tool
      try { localStorage.setItem(TOOL_KEY, tool) } catch { /* j.w. */ }
      app.querySelector<HTMLElement>('.ui')!.dataset.tool = tool
      toolBtns.forEach(o => o.setAttribute('aria-pressed', String(o === b)))
      // Zaznaczenia nie kasujemy - przejscie na przesuwanie ma pozwolic
      // obejrzec okolice zaznaczonego obiektu, a nie go zgubic.
    }
  })

  // ---- panel warstw
  // Legenda idzie osobnym zapytaniem: to kilkadziesiat kilobajtow ikon, a bez
  // niej lista warstw dziala tak samo, tylko bez podgladow.
  const legend: Legend = await api.legend(id).catch(e => { console.error(e); return {} })

  // Zgaszone na starcie: warstwy archiwalne (obiekty z data konca zaciemniaja
  // aktualny stan mapy, a bywa ich polowa listy) oraz granice administracyjne -
  // patrz CIEZKIE_GRANICE. Jedne i drugie wlacza sie jednym klknieciem.
  // Nazwy z zapisu przepuszczamy przez biezaca liste warstw: projekt mogl
  // zostac wczytany od nowa, z innym zestawem klas.
  const visible = new Set(zapamietanyWidok
    ? layers.filter(l => zapamietanyWidok.v.includes(l.name)).map(l => l.name)
    : layers.filter(l => !zgaszonaNaStarcie(l.name)).map(l => l.name))
  const onLayersChange = () => {
    reload()
    opsLayer.changed()
    syncArchSwitch()
    syncAllButton(visible)
    zapiszWidok()
  }
  buildRail(layers, visible, legend, onLayersChange)

  if (zapamietanyWidok?.lbl === false) opsLayer.showLabels = false
  document.getElementById('lbl')!.addEventListener('change', e => {
    opsLayer.showLabels = (e.target as HTMLInputElement).checked
    opsLayer.changed()
    zapiszWidok()
  })

  // ---- podklad
  // Wybor pamietany miedzy sesjami, tak jak warstwa w tabeli atrybutow. Kto raz
  // wlaczyl ortofotomape, zwykle chce ja miec i przy nastepnym pliku.
  const basemapSel = document.getElementById('basemap') as HTMLSelectElement
  // Bez zapamietanego wyboru wchodzi OpenStreetMap. Mapa zasadnicza sama w
  // sobie nie mowi, GDZIE sie jest - podklad daje to od pierwszego otwarcia,
  // bez szukania przelacznika. Kto wybierze 'brak', ma to zapamietane i
  // podklad juz mu nie wraca.
  const zapamietany = (restore(BASEMAP_KEY) ?? 'osm') as Podklad

  /**
   * Wlaczenie podkladu gasi bialy papier pod rysunkiem mapy zasadniczej.
   * Bez tego podklad wczytuje sie poprawnie i jest w komplecie niewidoczny,
   * bo plotno warstwy rysunku przykrywa go bialym prostokatem na caly ekran.
   */
  const ustawPodklad = (kind: Podklad) => {
    setBasemap(map, kind, projection)
    opsLayer.showPaper = kind === 'brak'
    opsLayer.changed()
  }

  const wybor: Podklad = PODKLADY.some(p => p.id === zapamietany) ? zapamietany : 'brak'
  basemapSel.value = wybor
  if (wybor !== 'brak') ustawPodklad(wybor)
  basemapSel.onchange = () => {
    const kind = basemapSel.value as Podklad
    store(BASEMAP_KEY, kind)
    ustawPodklad(kind)
  }

  // ---- globalny przelacznik warstw archiwalnych
  const archSwitch = document.getElementById('arch-vis') as HTMLInputElement | null

  /** Odbicie stanu pojedynczych checkboxow w przelaczniku zbiorczym. */
  function syncArchSwitch() {
    if (archSwitch) archSwitch.checked = archLayers.some(l => visible.has(l.name))
  }

  archSwitch?.addEventListener('change', () => {
    const on = archSwitch.checked
    for (const l of archLayers) {
      on ? visible.add(l.name) : visible.delete(l.name)
      const box = document.querySelector<HTMLInputElement>(`input[data-n="${CSS.escape(l.name)}"]`)
      if (box) {
        box.checked = on
        box.closest('.lyr')!.classList.toggle('on', on)
      }
    }
    onLayersChange()
  })

  // ---- doladowywanie po widoku
  let inflight: AbortController | null = null
  let timer: number | undefined

  const reload = () => {
    window.clearTimeout(timer)
    timer = window.setTimeout(doReload, 120)
  }

  async function doReload() {
    const view = map.getView()
    const res = view.getResolution()
    if (!res) return
    // Mianownik skali idzie tylko do zapytania - na ekranie pokazuje ja
    // podzialka OpenLayers w rogu mapy, powtarzanie go w belce bylo zbedne.
    const denom = res * RES_TO_DENOM

    inflight?.abort()
    inflight = new AbortController()
    const ext = view.calculateExtent(map.getSize())
    try {
      const data = await api.render(id, ext, denom, [...visible], inflight.signal)
      opsLayer.setData(data, visible)
    } catch (e) {
      if ((e as Error).name !== 'AbortError') console.error(e)
    }
  }

  map.on('moveend', reload)
  map.on('moveend', zapiszWidok)
  reload()

  // ---- tabela atrybutow
  //
  // Tabela i mapa dziela zaznaczenie w obie strony: wskazanie wiersza
  // podswietla obiekt na mapie i wypelnia panel atrybutow, a identyfikacja na
  // mapie podswietla wiersz, jesli akurat jest wczytany.
  const table = new AttrTable(document.getElementById('tbl')!, {
    projectId: id,
    layers,
    viewExtent: () => map.getView().calculateExtent(map.getSize()),
    onPick: (fid, layer) => showStack([{ layer, id: fid, dim: 0, metric: 0 }]),
    // minResolution: obwiednia budynku ma kilkanascie metrow, a punktu
    // graniczny zero - bez ograniczenia fit() zjechalby na skale rzedu 1:50,
    // gdzie nie ma juz co ogladac (najgestsze pasmo symboliki to 1:500).
    onZoom: ext => map.getView().fit(ext, {
      padding: [40, 40, 40, 40], duration: 320,
      minResolution: MIN_FIT_DENOM / RES_TO_DENOM,
    }),
    onResize: () => { map.updateSize(); reload() },
  })

  // Przy filtrze "tylko w widoku" tabela idzie za mapa; poza nim nic nie robi.
  map.on('moveend', () => table.viewChanged())

  // Guzik zestawien pokazuje sie dopiero, gdy projekt ma z czego je policzyc -
  // pusty guzik prowadzacy donikad byloby gorzej niz jego brak.
  const repBtn = document.getElementById('rep-btn')!
  table.hasReports().then(has => { repBtn.hidden = !has })
  repBtn.onclick = () => {
    table.showReports()
    tblOtwarta = table.open
    tblBtn.setAttribute('aria-pressed', String(table.open))
    zapiszWidok()
  }

  const tblBtn = document.getElementById('tbl-btn')!

  const syncTblBtn = () => {
    const label = table.open ? 'Ukryj tabelę atrybutów' : 'Pokaż tabelę atrybutów'
    tblBtn.setAttribute('aria-pressed', String(table.open))
    tblBtn.title = label
    tblBtn.setAttribute('aria-label', label)
    tblOtwarta = table.open
    zapiszWidok()
  }

  tblBtn.onclick = () => {
    // W trybie zestawienia ten guzik WRACA do tabeli klas, a nie chowa doku -
    // inaczej jedyna droga powrotu byloby zamkniecie i otwarcie od nowa.
    table.showTable()
    syncTblBtn()
  }

  // Dok otwarty przed odejsciem na liste wraca otwarty razem z projektem.
  if (tblOtwarta) {
    table.showTable()
    syncTblBtn()
  }

  setupGrips(app, () => { map.updateSize(); reload() })

  // ---- identyfikacja obiektow
  //
  // Klikniecie bierze CALY stos obiektow pod kursorem, nie tylko wierzchni.
  // Poligony mapy zasadniczej rutynowo leza jeden na drugim (uzytek na
  // dzialce, dzialka pod budynkiem) i przy jednym trafieniu do nizszych nie
  // ma jak dojsc - QGIS pokazuje w takiej sytuacji liste wynikow, wiec i tu
  // panel zaczyna sie od listy trafien.
  const panelEl = document.getElementById('panel')!
  const PLACEHOLDER = '<div class="ph">Kliknij obiekt na mapie, aby zobaczyć jego atrybuty.</div>'

  /** Ile pozycji stosu pokazujemy - glebiej lista przestaje byc do przejrzenia. */
  const MAX_HITS = 12

  let stack: PickHit[] = []          // trafienia od wierzchu w dol
  let hitCount = 0                   // ile ich bylo naprawde (stack bywa przyciety)
  let active = 0                     // ktore z nich jest wybrane
  let loading = false
  let clickSeq = 0                   // odsiewa odpowiedzi po nastepnym klknieciu
  // (zwykly obiekt, a nie Map - ta nazwa jest tu zajeta przez ol/Map)
  let details: Record<number, FeatureDetail | undefined> = {}

  const clearSelection = () => {
    stack = []
    hitCount = 0
    active = 0
    loading = false
    details = {}
    opsLayer.selected = null
    opsLayer.hover = null
    panelEl.innerHTML = PLACEHOLDER
    opsLayer.changed()
    table.markSelected(null)
  }

  window.addEventListener('keydown', e => {
    if (e.key === 'Escape' && stack.length) clearSelection()
  })

  map.on('singleclick', evt => {
    if (tool !== 'identify') return
    const res = map.getView().getResolution() ?? 1
    const hits = opsLayer.pickAll(evt.coordinate[0], evt.coordinate[1], 8 * res)
    if (!hits.length) { clearSelection(); return }
    showStack(hits)
  })

  /**
   * Pokazuje stos trafien: zaznaczenie wierzchniego, liste i jego atrybuty.
   * Wolane i z mapy, i z tabeli atrybutow (tam stos ma zawsze jeden element).
   */
  async function showStack(hits: PickHit[]) {
    const seq = ++clickSeq
    stack = hits.slice(0, MAX_HITS)
    hitCount = hits.length
    active = 0
    loading = true
    details = {}
    opsLayer.selected = stack[0].id
    opsLayer.hover = null
    opsLayer.changed()
    table.markSelected(stack[0].id)
    drawPanel()                      // lista trafien jest znana od razu

    try {
      const got = await api.features(id, stack.map(h => h.id))
      if (seq !== clickSeq) return    // w miedzyczasie padlo kolejne klikniecie
      for (const d of got) details[d.id] = d
    } catch (e) {
      if (seq !== clickSeq) return
      console.error(e)
    }
    loading = false
    drawPanel()
  }

  /** Wybor pozycji ze stosu - zaznaczenie na mapie i atrybuty w panelu. */
  function selectHit(i: number) {
    if (!stack[i]) return
    active = i
    opsLayer.selected = stack[i].id
    opsLayer.hover = null
    opsLayer.changed()
    table.markSelected(stack[i].id)
    drawPanel()
  }

  function drawPanel() {
    const hit = stack[active]
    if (!hit) { panelEl.innerHTML = PLACEHOLDER; return }
    const d = details[hit.id]

    const body = d
      ? `<dl class="p-b">${d.attrs.map(a =>
          `<div><dt>${esc(a.k)}</dt><dd class="mono">${esc(String(a.v))}</dd></div>`).join('')
          || '<div class="ph">Obiekt bez atrybutów.</div>'}</dl>`
      : `<div class="ph">${loading ? 'Wczytywanie…' : 'Nie udało się wczytać atrybutów.'}</div>`

    panelEl.innerHTML = `
      ${stack.length > 1 ? stackHTML() : ''}
      <div class="p-h"><b>${esc(d?.layer ?? hit.layer)}</b>
        <span class="mono">${esc(d?.gml_id ?? `#${hit.id}`)}</span></div>
      ${body}`
    wireStack()
  }

  /** Lista trafien - widoczna tylko wtedy, gdy jest z czego wybierać. */
  function stackHTML(): string {
    const more = hitCount > stack.length
      ? ` <span class="s-more">(pokazano ${stack.length})</span>` : ''
    return `
      <div class="p-stack">
        <div class="s-h">Pod kursorem: <b>${plural(hitCount, OBIEKT)}</b>${more}</div>
        <div class="s-list" role="listbox" aria-label="Obiekty pod kursorem">
          ${stack.map((h, i) => {
            const d = details[h.id]
            return `<button type="button" class="s-item${i === active ? ' on' : ''}"
                      role="option" aria-selected="${i === active}"
                      tabindex="${i === active ? 0 : -1}" data-i="${i}">
              <span class="s-nm">${esc(d?.layer ?? h.layer)}</span>
              <span class="s-id mono">${esc(d?.title ?? d?.gml_id ?? `#${h.id}`)}</span>
            </button>`
          }).join('')}
        </div>
      </div>`
  }

  function wireStack() {
    panelEl.querySelectorAll<HTMLButtonElement>('.s-item').forEach(b => {
      const i = Number(b.dataset.i)
      // Najechanie pokazuje na mapie, o ktory obiekt chodzi - bez tego lista
      // trzech dzialek nie mowi nic o tym, ktora jest ktora.
      const preview = (on: boolean) => {
        opsLayer.hover = on ? stack[i].id : null
        opsLayer.changed()
      }
      b.onclick = () => selectHit(i)
      b.onpointerenter = () => preview(true)
      b.onpointerleave = () => preview(false)
      b.onfocus = () => preview(true)
      b.onblur = () => preview(false)
      b.onkeydown = e => {
        const step = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0
        if (!step) return
        e.preventDefault()
        const next = (i + step + stack.length) % stack.length
        selectHit(next)
        panelEl.querySelector<HTMLButtonElement>(`.s-item[data-i="${next}"]`)?.focus()
      }
    })
  }
}


function buildRail(layers: LayerInfo[], visible: Set<string>, legend: Legend,
                   onChange: () => void) {
  const box = document.getElementById('lyrs')!
  const sorted = layers      // API oddaje juz kolejnosc z drzewa warstw, od wierzchu

  // Grupujemy po nazwie grupy, a nie po sasiedztwie: legenda potrafi wrocic do
  // tej samej grupy nizej (np. rejestrowe klasy EGiB za sieciami GESUT), a dwa
  // pudelka o tej samej nazwie w panelu wygladalyby na blad.
  // (nazwa Map jest tu zajeta przez ol/Map, stad tablica par.)
  const groups: [string, LayerInfo[]][] = []
  for (const l of sorted) {
    const g = l.group || 'Pozostałe'
    const hit = groups.find(([n]) => n === g)
    if (hit) hit[1].push(l)
    else groups.push([g, [l]])
  }

  box.innerHTML = groups.map(([g, ls], i) => `
    <section class="grp-box">
      <button class="grp" type="button" aria-expanded="true" aria-controls="grp-${i}">
        ${ICON.chevron}
        <span class="grp-nm">${g}</span>
        <span class="grp-ct mono">${ls.length}</span>
      </button>
      <div class="grp-body" id="grp-${i}">${
        ls.map(l => layerRow(l, legend[l.name] ?? [], visible.has(l.name))).join('')}</div>
    </section>`).join('')

  // Rozwijanie klas symboliki. Klasy sluza tylko do ogladania - wypieczone
  // instrukcje nie niosa informacji o tym, ktora regula je wygenerowala, wiec
  // pojedynczej klasy nie da sie zgasic.
  box.querySelectorAll<HTMLButtonElement>('.exp').forEach(b => {
    b.onclick = () => {
      const open = b.getAttribute('aria-expanded') !== 'true'
      b.setAttribute('aria-expanded', String(open))
      const list = b.closest('.lyr')!.nextElementSibling as HTMLElement | null
      if (list?.classList.contains('cls')) list.hidden = !open
    }
  })

  // Zwijanie grup dotyka tylko widoku listy - warstwa schowanej grupy zostaje
  // na mapie, jej checkbox po prostu nie jest w tej chwili widoczny.
  const headers = box.querySelectorAll<HTMLButtonElement>('.grp')
  headers.forEach(b => {
    b.onclick = () => b.setAttribute('aria-expanded',
      String(b.getAttribute('aria-expanded') !== 'true'))
  })
  // "Zwin/rozwin wszystko" obejmuje takze klasy symboliki - rozwiniete grupy
  // bez widocznej legendy nie sa jeszcze calym drzewem.
  const openAll = (open: boolean) => {
    headers.forEach(b => b.setAttribute('aria-expanded', String(open)))
    box.querySelectorAll<HTMLButtonElement>('.exp').forEach(b => {
      b.setAttribute('aria-expanded', String(open))
      const list = b.closest('.lyr')!.nextElementSibling as HTMLElement | null
      if (list?.classList.contains('cls')) list.hidden = !open
    })
  }
  document.getElementById('collapse')!.onclick = () => openAll(false)
  document.getElementById('expand')!.onclick = () => openAll(true)

  box.querySelectorAll<HTMLInputElement>('input[data-n]').forEach(i => {
    i.onchange = () => {
      const n = i.dataset.n!
      i.checked ? visible.add(n) : visible.delete(n)
      i.closest('.lyr')!.classList.toggle('on', i.checked)
      onChange()
    }
  })

  document.getElementById('all')!.onclick = () => {
    const any = visible.size > 0
    visible.clear()
    if (!any) sorted.forEach(l => visible.add(l.name))
    box.querySelectorAll<HTMLInputElement>('input[data-n]').forEach(i => {
      i.checked = !any
      i.closest('.lyr')!.classList.toggle('on', !any)
    })
    onChange()
  }

  syncAllButton(visible)
}

const SWATCH_PX = 22

/**
 * Wiersz warstwy z rozwijana lista klas symboliki.
 *
 * Kazda warstwa ze stylem dostaje ten sam, zwiniety uklad - takze ta z jedna
 * klasa. Podglad wstawiony wprost w wiersz robil z listy warstw szachownice
 * kartonikow i lamal rowna kolumne nazw.
 */
function layerRow(l: LayerInfo, items: LegendItem[], on: boolean): string {
  const id = `lyr-${l.name}`
  const swatch = (it: LegendItem) =>
    `<img class="sw" src="${it.icon}" alt="" width="${SWATCH_PX}" height="${SWATCH_PX}">`

  const slot = items.length
    ? `<button class="exp" type="button" aria-expanded="false"
              title="Symbolika: ${items.length} ${items.length === 1 ? 'klasa' : 'klas'}"
              aria-label="Klasy symboliki warstwy ${esc(l.name)}">${ICON.chevron}</button>`
    : '<span class="sw-none"></span>'

  const classes = items.length ? `
    <div class="cls" hidden>${items.map(it =>
      `<div class="cls-row">${swatch(it)}<span>${esc(it.label)}</span></div>`).join('')}</div>` : ''

  return `
    <div class="lyr${on ? ' on' : ''}${isArch(l.name) ? ' arch' : ''}">
      <input type="checkbox" id="${id}"${on ? ' checked' : ''} data-n="${esc(l.name)}">
      ${slot}
      <label class="nm" for="${id}"${CIEZKIE_GRANICE.has(l.name)
        ? ' title="Granica administracyjna — domyślnie zgaszona, bo jej symbolika'
          + ' bardzo spowalnia mapę. Można ją włączyć."' : ''}>${esc(l.name)}</label>
      <span class="ct mono">${nf.format(l.feature_count)}</span>
    </div>${classes}`
}

const BASEMAP_KEY = 'gmlweb.basemap'

/** Warstwa archiwalna - reader dokleja _ARCH obiektom z data konca. */
const isArch = (name: string) => name.endsWith('_ARCH')

/**
 * Granice jednostki ewidencyjnej i obrebu. Startuja zgaszone z dwoch powodow.
 *
 * Rzadko sa komukolwiek potrzebne - to podklad administracyjny, a nie tresc
 * mapy zasadniczej, po ktora ktos otwiera plik GML.
 *
 * I sa nieproporcjonalnie drogie. Granica jest w symbolice kropkowana, czyli
 * rysowana markerami stawianymi co ~2,5 m wzdluz calego obwodu: dwa obiekty
 * w pliku testowym to 106 871 markerow i 16 MB instrukcji, wiecej niz cala
 * reszta projektu razem wzieta. Obwiednia takiego obiektu obejmuje przy tym
 * caly zbior, wiec filtr po widoku go nie odsiewa i te 15 MB szlo do
 * przegladarki przy KAZDYM przesunieciu mapy.
 *
 * To obejscie, nie naprawa. Wlasciwe rozwiazanie to podzial wypieczonych
 * instrukcji na kawalki przestrzenne albo wypiekanie wzoru zamiast pojedynczych
 * kropek - obie drogi zostaja do zrobienia.
 */
const CIEZKIE_GRANICE = new Set([
  'EGB_JednostkaEwidencyjna_powierzchnia',
  'EGB_ObrebEwidencyjny_powierzchnia',
])

/** Czy warstwa ma byc zgaszona zaraz po otwarciu projektu. */
const zgaszonaNaStarcie = (name: string) =>
  isArch(name) || CIEZKIE_GRANICE.has(name)

/**
 * Guzik "wszystkie warstwy": ikona pokazuje STAN (oko otwarte = cos widac),
 * a podpowiedz - co zrobi klikniecie. Sama para strzalek nie mowila ani
 * jednego, ani drugiego.
 */
function syncAllButton(visible: Set<string>) {
  const b = document.getElementById('all')
  if (!b) return
  const on = visible.size > 0
  b.innerHTML = on ? ICON.eye : ICON.eyeOff
  const label = on ? 'Ukryj wszystkie warstwy' : 'Pokaż wszystkie warstwy'
  b.title = label
  b.setAttribute('aria-label', label)
}

boot()
